import styled from "@emotion/styled";
import { useMemo } from "react";

import { FormRow, IconButton, InfoTooltip, Labelish, Tooltip } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { useElementStatePlainLabels } from "./shared.tsx";
import type { ElementStateKey, StylesByElementState } from "./types";

type Props = {
  onRemove: (elementStateKey: ElementStateKey, otherNodeRefKey?: string) => void;
  stylesByElementState: StylesByElementState;
  onSelectNodeRef: (nodeRef: string) => void;
};

export const ElementStateOverridesList = ({ stylesByElementState, onRemove, onSelectNodeRef }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const elementStateLabels = useElementStatePlainLabels();

  const { overrideStyles, ancestorOverrideStyles } = useMemo(() => {
    const elementStateOverrides = Object.entries(stylesByElementState)
      .filter(([_, value]) => value && value?.otherNodeRefKey === undefined)
      .flatMap(([key, value]) => ({
        label: value?.state ? elementStateLabels[value?.state] : "",
        elementStateKey: key as ElementStateKey,
      }));

    const ancestorElementStateOverrides = Object.entries(stylesByElementState)
      .filter((arr): arr is [string, NonNullable<(typeof arr)[1]>] => !!arr[1]?.otherNodeRefKey)
      .flatMap(([key, value]) => ({
        label: value.state ? elementStateLabels[value.state] : "",
        elementStateKey: key as ElementStateKey,
        otherNodeRefKey: value.otherNodeRefKey,
      }));

    return {
      overrideStyles: elementStateOverrides,
      ancestorOverrideStyles: ancestorElementStateOverrides,
    };
  }, [stylesByElementState, elementStateLabels]);

  const handleLabelClick = (otherNodeRefKey?: string) => {
    if (otherNodeRefKey) {
      onSelectNodeRef(otherNodeRefKey);
    }
  };

  return (
    <>
      {!!overrideStyles.length && (
        <OverrideRowsContainer>
          <Labelish>
            {formatMessage("overrideStyles")} <InfoTooltip tooltip={formatMessage("overrideStylesTooltip")} />
          </Labelish>
          {overrideStyles.map(({ label, elementStateKey }) => (
            <RowItem key={elementStateKey} label={label} onRemove={() => onRemove(elementStateKey)} />
          ))}
        </OverrideRowsContainer>
      )}

      {!!ancestorOverrideStyles.length && (
        <OverrideRowsContainer>
          <Labelish>
            {formatMessage("inheritedStyles")} <InfoTooltip tooltip={formatMessage("inheritedStylesTooltip")} />
          </Labelish>
          {ancestorOverrideStyles.map(({ label, elementStateKey, otherNodeRefKey }) => (
            <RowItem
              key={elementStateKey}
              label={label}
              onLabelClick={() => handleLabelClick(otherNodeRefKey)}
              tooltipLabel={formatMessage("goToParentContainer")}
              onRemove={() => onRemove(elementStateKey, otherNodeRefKey)}
            />
          ))}
        </OverrideRowsContainer>
      )}
    </>
  );
};

const OverrideRowsContainer = styled(FormRow)`
  margin-top: 20px;
`;

type RowItemProps = {
  label?: string;
  onRemove: () => void;
  onLabelClick?: () => void;
  tooltipLabel?: string;
};

const RowItem = ({ label, onRemove, onLabelClick, tooltipLabel }: RowItemProps) => {
  return (
    <RowItemContainer>
      {onLabelClick ? (
        <Tooltip title={tooltipLabel} placement="left">
          <InheritedStyleLink onClick={onLabelClick}>{label}</InheritedStyleLink>
        </Tooltip>
      ) : (
        label
      )}
      <IconButton onClick={onRemove} icon="minus" data-test="remove-element-state-button" />
    </RowItemContainer>
  );
};

const RowItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InheritedStyleLink = styled.span`
  cursor: pointer;
  transition: color 0.2s ease-in-out;
  &:hover {
    color: ${({ theme }) => theme.primaryStrong};
  }
`;

const formatStrings = {
  overrideStyles: {
    en: "Override Styles",
  },
  overrideStylesTooltip: {
    en: "List of state changes that will override the default state for this element.",
  },
  inheritedStyles: {
    en: "Inherited Styles",
  },
  inheritedStylesTooltip: {
    en: "List of parent state changes that will apply override styles to this element.",
  },
  goToParentContainer: {
    en: "Go to parent element",
  },
};
